/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

const DEFAULT_COOKIE_AGE = 60 * 60 * 24 * 365;

/**
 * @typedef SetOptions
 */
interface SetOptions {
  domain?: string;
  maxAge?: number;
}

/**
 * Delete a cookie.
 *
 * @param {Window} global Window object.
 * @param {Document} global.document Document object.
 * @param {string} name Name of the cookie to delete.
 */
export const remove = (global: Window & typeof globalThis, name: string): void => {
  set(global, name, '', {maxAge: 0});
};

/**
 * Set a cookie.
 *
 * @param {Window} global Window object.
 * @param {Document} global.document Document object.
 * @param {string} name Name of the cookie to set.
 * @param {string} value Value of the cookie to set.
 * @param {SetOptions} options Configuration options for the cookie.
 */
export const set = (
  {document}: Window & typeof globalThis,
  name: string,
  value: string,
  options: SetOptions = {},
): void => {
  if (typeof document?.cookie !== 'string') {
    return;
  }
  const {domain, maxAge = DEFAULT_COOKIE_AGE} = options;
  const cookieDomain = domain ? `;domain=${domain}` : '';
  const cookieMaxAge = `;max-age=${maxAge}`;
  document.cookie = `${name}=${value};path=/${cookieDomain}${cookieMaxAge}`;
};
